import { DIMS, TIER_COLORS, TIER_NAME, tierOf, hasDim, byId, REGIONS, REGION_VIEW } from './model.js';
import { renderLegend, applyFilters, setRegionFilter } from './ui/legend.js';
import { initStory } from './ui/story.js';
import { initRail } from './ui/rail.js';
import { initRoute } from './ui/route.js';
import { initDrawer, openCountry, openRegion, openMethod, closeDrawer } from './ui/drawer.js';
import './ui/search.js';
import { cam, initThree, flyToLL, buildBeacons, raycastLatLon, setDragging, isDragging } from './globe/scene.js';
import { mapView, metricOf, repaint, setHover, idAt, loadIndexMap } from './globe/texture.js';
import { h } from './untrusted.js';

const MAPIMG = '/map-index.png';
history.scrollRestoration = 'manual';   // scroll-driven story: always start at the hero

/* ================= loader ================= */
const lbar=document.getElementById('lbar'), lst=document.getElementById('lst');
function stage(p,t){ lbar.style.width=(p*100)+'%'; lst.textContent=t }

/* ================= boot ================= */
loadIndexMap(MAPIMG, stage).then(() => {
  stage(.72, 'Building the globe');
  bindPointer(initThree());
  stage(1, 'Ready');
  initRoute();   // only now can a panel paint a selection onto the globe
  setTimeout(() => document.getElementById('loader').classList.add('done'), 450);
});



/* ================= pointer: rotate / hover / click ================= */
function pick(e){
  const ll = raycastLatLon(e.clientX, e.clientY);
  if(!ll) return null;
  const [lat,lon]=ll;
  return {lat,lon,id:idAt(lat,lon)};
}
const tip=document.getElementById('tip'), coordsEl=document.getElementById('coords');
function bindPointer(cv){
  let start=null;
  cv.addEventListener('pointerdown', e=>{
    if(!document.body.classList.contains('explorer')) return;
    start={x:e.clientX,y:e.clientY,lat:cam.tLat,lon:cam.tLon,moved:false};
    setDragging(true); cv.setPointerCapture(e.pointerId); cv.classList.add('panning');
  });
  cv.addEventListener('pointermove', e=>{
    if(isDragging()&&start){
      const dx=e.clientX-start.x, dy=e.clientY-start.y;
      if(Math.abs(dx)+Math.abs(dy)>5) start.moved=true;
      const s=.22*Math.min(1,(cam.dist-1)/1.6)+.04;
      cam.tLon=start.lon - dx*s; cam.tLat=Math.min(80,Math.max(-80,start.lat + dy*s));
      tip.style.display='none';
      return;
    }
    if(!document.body.classList.contains('explorer')) return;
    const hit=pick(e);
    if(hit&&hit.id>1&&byId[hit.id]){
      const c=byId[hit.id];
      setHover(hit.id);
      coordsEl.textContent=c.n.toUpperCase()+' · '+c.r.toUpperCase();
      tip.innerHTML=String(h`<div class="tr"><span>${c.r}</span><span class="rk">${mapView.colorMode==='overall'?'':mapView.colorMode+' · '}${metricOf(c)==null?'No data':TIER_NAME[tierOf(metricOf(c))]}</span></div><div class="tn">${c.n}</div>
        <div class="bars">${DIMS.map(d=>{ const v=c.dim[d];
          return v==null ? h`<i title="${d+' - not collected'}" style="background:#3c4457;height:15%"></i>`
                         : h`<i title="${d}" style="background:${TIER_COLORS[tierOf(v)]};height:${22+v/4*78}%"></i>` })}</div>
        <div class="hint">Click — full profile</div>`);
      tip.style.display='block';
      const tw2=tip.offsetWidth, th2=tip.offsetHeight;
      let x=e.clientX+18, y=e.clientY+16;
      if(x+tw2>innerWidth-12) x=e.clientX-tw2-16;
      if(y+th2>innerHeight-12) y=e.clientY-th2-14;
      tip.style.left=x+'px'; tip.style.top=y+'px';
    } else {
      setHover(0); tip.style.display='none'; coordsEl.textContent='';
    }
  });
  cv.addEventListener('pointerup', e=>{
    const wasDrag=start&&start.moved; setDragging(false); cv.classList.remove('panning');
    if(!wasDrag && document.body.classList.contains('explorer')){
      const hit=pick(e);
      if(hit&&hit.id>1&&byId[hit.id]) openCountry(byId[hit.id]);
    }
    start=null;
  });
  cv.addEventListener('pointerleave', ()=>{ setHover(0); tip.style.display='none' });
  cv.addEventListener('wheel', e=>{
    if(!document.body.classList.contains('explorer')) return;
    e.preventDefault();
    cam.tDist=Math.min(4.2, Math.max(1.45, cam.tDist*(e.deltaY>0?1.09:1/1.09)));
  },{passive:false});
}
document.getElementById('zin').onclick=()=> cam.tDist=Math.max(1.45,cam.tDist/1.25);
document.getElementById('zout').onclick=()=> cam.tDist=Math.min(4.2,cam.tDist*1.25);
document.getElementById('zreset').onclick=()=>{ cam.tDist=2.6; cam.tLat=25; };


/* legend, filters and the story live in ui/; main.js only wires them up */
renderLegend();

/* colour-by-dimension map modes */
const mc=document.getElementById('modechips');
mc.innerHTML = String(h`<button class="tab on" data-m="overall">Overall</button>${
  DIMS.map(d=>h`<button class="tab${hasDim(d)?'':' nodata'}" data-m="${d}" title="${hasDim(d)?d:d+' — no data in this delivery'}">${d}</button>`)}`);
mc.querySelectorAll('.tab').forEach(b=> b.onclick=()=>{
  mc.querySelectorAll('.tab').forEach(x=>x.classList.remove('on')); b.classList.add('on');
  mapView.colorMode = b.dataset.m;
  repaint(true); renderLegend(); applyFilters(); buildBeacons();
});
document.getElementById('methodBtn').onclick=()=>openMethod();

/* theme toggle: light skins the UI surfaces only — the 3D scene stays dark by design */
(function theme(){
  let saved=null;
  try{ saved=localStorage.getItem('sei-theme') }catch(e){}
  if(saved==='light') document.documentElement.dataset.theme='light';
  document.getElementById('themeBtn').onclick=()=>{
    const light = document.documentElement.dataset.theme==='light';
    if(light) delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme='light';
    try{ localStorage.setItem('sei-theme', light?'dark':'light') }catch(e){}
  };
})();

const rc=document.getElementById('regionchips');
rc.innerHTML = String(h`<button class="tab on" data-r="">All</button>${
  REGIONS.map(r=>h`<button class="tab" data-r="${r}">${r}</button>`)}`);
rc.querySelectorAll('.tab').forEach(b=> b.onclick=()=>{
  rc.querySelectorAll('.tab').forEach(x=>x.classList.remove('on')); b.classList.add('on');
  const region=b.dataset.r||null; setRegionFilter(region);
  if(region){ const v=REGION_VIEW[region]; if(v) flyToLL(v[0],v[1],2.3); openRegion(region) }
  else closeDrawer();
});


/* clock */
(function(){ const el=document.getElementById('utc');
  const tick=()=>{ el.textContent=new Date().toISOString().slice(11,19)+' UTC' };
  tick(); setInterval(tick,1000);
})();
initDrawer();
initRail();
initStory();
